import { normalizeContainerStatus } from './container-status-presentation.mjs?v=20260806.100';

const COMPLETE = new Set(['empty_returned', 'empty_returned_to_terminal', 'completed']);
const CANCELLED = new Set(['cancelled', 'canceled']);

function dateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value : null;
  if (typeof value.toDate === 'function') return dateValue(value.toDate());
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function status(row) {
  return normalizeContainerStatus(row?.ocean_status || row?.status || row?.current_stage || '');
}

function isComplete(row) {
  return row?.canonical_completed === true || row?.is_empty_returned === true || COMPLETE.has(status(row));
}

// The strip reads by week (Alan, 2026-08-03). Drayage moves in days: a month-wide
// bucket flattened the only shape anyone was looking for, which is what this week
// did against the last few. Weeks start Monday, and the key is the Monday's date so
// two dates in the same week land in the same bucket regardless of time of day.
const WEEK_MILLIS = 7 * 24 * 60 * 60 * 1000;

function weekStart(date) {
  if (!date) return null;
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const weekday = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - weekday);
  return start;
}

function weekKey(date) {
  const start = weekStart(date);
  return start
    ? `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`
    : '';
}

function numeric(row, ...keys) {
  for (const key of keys) {
    const value = Number(row?.[key]);
    if (Number.isFinite(value)) return value;
  }
  return 0;
}

export function containerKpiMonthlySeries({ rows = [], now = new Date(), locale = 'en-US' } = {}) {
  const safeNow = dateValue(now) || new Date();
  const thisWeek = weekStart(safeNow);
  const weeks = Array.from({ length: 6 }, (_, index) => new Date(
    thisWeek.getTime() - ((5 - index) * WEEK_MILLIS),
  ));
  const keys = weeks.map(weekKey);
  const indexByKey = new Map(keys.map((key, index) => [key, index]));
  const label = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
  const series = {
    labels: weeks.map((week) => label.format(week)),
    total: Array(6).fill(0),
    active: Array(6).fill(0),
    action: Array(6).fill(0),
    risk: Array(6).fill(0),
    completed: Array(6).fill(0),
    due: Array(6).fill(0),
    profit: Array(6).fill(0),
  };

  const add = (name, date, amount = 1) => {
    const index = indexByKey.get(weekKey(date));
    if (index !== undefined) series[name][index] += amount;
  };

  rows.forEach((row) => {
    const createdAt = dateValue(row?.created_at) || dateValue(row?.updated_at);
    const activityAt = dateValue(row?.updated_at) || createdAt;
    const completeAt = dateValue(row?.empty_returned_at) || dateValue(row?.returned_at)
      || dateValue(row?.closed_at) || activityAt;
    const dueAt = dateValue(row?.pickup_appointment_at) || dateValue(row?.delivery_appointment_at);
    const complete = isComplete(row);
    const cancelled = CANCELLED.has(status(row));
    add('total', createdAt);
    // Active is work that was released to a driver, matching the card above it.
    const released = dateValue(row?.dispatch_released_at);
    if (!complete && !cancelled && released) add('active', activityAt);
    if (!complete && !cancelled && (row?.action_required === true || row?.needs_action === true)) add('action', activityAt);
    // At Risk is overdue and only overdue, matching the card above it.
    if (!complete && !cancelled && (row?.is_past_due === true || status(row) === 'past_due')) add('risk', activityAt);
    if (complete) {
      add('completed', completeAt);
      add('profit', completeAt, numeric(row, 'profit_cents'));
    }
    add('due', dueAt);
  });

  return Object.freeze(Object.fromEntries(Object.entries(series).map(([key, value]) => [key, Object.freeze(value)])));
}
